<template>
  <FilterNoResults v-if="isNoResults" @on-close="isNoResults = false" />
  <!-- Sticky + breakout on same wrapper: sticky must not share a node with overflow-hidden (breaks WebKit/Chromium). -->
  <div
    class="filters-bar -mx-4 md:mx-0 sticky top-[115px] md:top-[--kft-navbar-height] bg-white sm:block"
    :class="zIndex"
  >
    <ClientOnly>
      <template #fallback>
        <div class="px-4 md:px-0">
          <FilterBarSkeleton />
        </div>
      </template>
      <div class="px-4 md:px-0">
        <div class="filter-wrap">
          <Filters
            :facets="filterOptions"
            :filters="filters"
            @filters-updated="handleFiltersUpdate"
          />

          <FiltersSelected
            :city-label="currentLocality?.name"
            @filters-updated="handleFiltersUpdate"
          />
        </div>
        <div class="flex justify-between mt-0 pb-2">
          <FilterResultCount
            class="text-xs text-gray-500"
            :hits-length="searchResults?.nbHits"
            :filters="{
              ...filters,
              locality: {
                name: filters.city,
              },
              categories: filters.categories,
            }"
            :current-locality="currentLocality?.name"
            :is-no-results="isNoResults"
          />
          <FilterOrderBy />
        </div>
      </div>
    </ClientOnly>
  </div>
  <!-- :query-id="topEventsQueryID" -->
  <ProductShowcase
    id="filteredProductShowCasePageTop"
    :algolia-insights-client="algoliaInsightsClient"
    data-test-id="top-events-section"
    :products="searchResults?.hits || []"
    image-loading="lazy"
    :tags="[]"
    :loading="loading"
    :algolia-insights-label="algoliaEventName"
    :use-h-tag="false"
    :query-id="algoliaQueryId"
    :showcase-container-data-test-id="'filtered-product-showcase-results'"
    :special-giftcard-event="specialGiftcardEvents[0]"
    class="mt-4"
  >
    <template #promotion2>
      <ProductCard
        class="promotion-card cursor-pointer"
        :title="$t('general.promotionCards.bookingSolutionGiftcard.label')"
        :link="'/giftcard'"
        :tags="
          $t('product.promo.giftcard', { eventCount: eventsCount }).split('; ')
        "
        image="/_nuxt3-static/promo-cards/giftbox.webp"
        is-promo-card
      />
    </template>
    <template #promotion1>
      <ErrorBoundary>
        <ProductCard
          class="promotion-card cursor-pointer"
          :title="$t('general.promotionCards.teamevents.label')"
          link="javascript:void(0)"
          :tags="$t('product.promo.teamevent').split('; ')"
          image="/_nuxt3-static/promo-cards/team-event.webp"
          is-promo-card
          @click="toggleDateRequestSidebar(route.name, 'search_results')"
        />
      </ErrorBoundary>
    </template>
  </ProductShowcase>
  <FilterPagination
    :hits-per-page="searchResults?.hitsPerPage ?? 0"
    :nb-hits="searchResults?.nbHits ?? 0"
    :nb-pages="searchResults?.nbPages"
    @updated="handleChangePage"
  />
</template>
<script setup lang="ts">
import type { Hit } from '@nuxtjs/algolia'
import { useRouteParams, useRouteQuery } from '@vueuse/router'
import isEqual from 'lodash/isEqual'
import cloneDeep from 'lodash/cloneDeep'
import useAlgoliaEventSchema from '~/composables/schemaOrg/useAlgoliaEventSchema'
import useTransactionalPageHead from '~/composables/transactionalPage/useTransactionalPageHead'
import {
  useTransactionalPageAlgoliaSearch,
  useTransactionalPageAlgoliaSearchSideEffects,
} from '~/composables/useTransactionalPageAlgolia'
import type { Category } from '~/composables/types/event'
import {
  EventListPageType,
  type StrapiEventListPage,
} from '~/composables/types/strapiEventListPage'

import type { AlgoliaCity, AlgoliaEventItem } from '~/composables/types/algolia'
import { getAlgoliaSearchOptions } from '~/utils/algoliaHelpers'
import type {
  CategoryTypes,
  SearchFilterOptions,
} from '~/composables/useSearchFilters'
import { useTransactionalProductSchema } from '~/composables/schemaOrg/useTransactionalProductSchema'
import useAlgoliaEventCarouselSchema from '~/composables/schemaOrg/useAlgoliaEventCarouselSchema'
import { eventListPageCategoryUrl } from '~/components/transactionalPage/requestParams'
import { useAlgoliaSearchInsights } from '~/composables/algolia/useAlgoliaSearchInsights'
import { useAlgoliaIndexNames } from '~/composables/algolia/useAlgoliaIndexNames'
import { HITS_PER_PAGINATION } from '~/composables/algolia/constants'
import { NUMBER_OF_EVENTS } from '~/composables/product/constants'
import { sendFilterSelected } from '~/composables/useGtmEvents'

const route = useRoute()
const router = useRouter()
const citySlugParam = useRouteParams('city')
const citySlugQuery = useRouteQuery('city')
const categorySlugParam = useRouteParams('category')
const tagSlugParam = useRouteParams('slug')
const { toggleDateRequestSidebar } = useUiState()

const isNoResults = ref(false)
provide('noResultsVisible', isNoResults)

const citySlug = computed(() =>
  (citySlugQuery.value || citySlugParam.value)?.toString()
)

const categorySlug = computed(() => categorySlugParam.value?.toString())

const props = withDefaults(
  defineProps<{
    pageType: EventListPageType
    eventListPageData?: StrapiEventListPage
    loadSchema?: boolean
    reviewsData?: {
      reviews: any[]
      averageRating: string
      reviewCount: number
    } | null
  }>(),
  {
    eventListPageData: undefined,
    loadSchema: false,
    reviewsData: null,
  }
)

const { client: algoliaInsightsClient } = useAlgoliaSearchInsights()

const PAGE_KEY = route.path

// Generate human-readable Algolia event name based on page type
const algoliaEventName = computed(() => {
  switch (props.pageType) {
    case EventListPageType.SEARCH:
      return 'Search Page'
    case EventListPageType.CITY:
      return 'City Page'
    case EventListPageType.CATEGORY:
      return 'Category Page'
    case EventListPageType.CITY_CATEGORY:
      return 'City Category Page'
    case EventListPageType.TEAMEVENT:
      return 'Teamevent Page'
    case EventListPageType.KITS:
      return 'Kits Page'
    case EventListPageType.GENERAL:
    default:
      return 'General Page'
  }
})

const eventListPageAttributes = computed(() => {
  return props.eventListPageData
})
const { localities: localitiesIndex } = useAlgoliaIndexNames()

const { result: localitiesAlgolia } =
  useAlgoliaSearch<AlgoliaCity>(localitiesIndex)

provide('localitiesAlgolia', localitiesAlgolia)

const { data: categoryResults, execute: searchCategories } =
  await useKftApiFetch<{
    data: Category
  }>(eventListPageCategoryUrl({ categorySlug: categorySlug.value }))

const initialCategoryName = computed(() => categoryResults.value?.data?.name)

const { locale, t } = useI18n()

const indexName =
  props.pageType === EventListPageType.SEARCH
    ? getFederatedSearchIndexName(locale.value)
    : getEventListIndexName(locale.value)

const defaultFilters = computed<Partial<SearchFilterOptions>>(() => ({
  ...(initialCategoryName.value && { categories: [initialCategoryName.value] }),
  city: citySlug.value,
}))

const { filters } = useSearchFilters(PAGE_KEY, defaultFilters)
provide('productsFilters', filters)

const { filteredGiftcardEvents: specialGiftcardEvents } =
  await useGiftcardEvents(filters)

const currentLocality = computed(
  () =>
    localitiesAlgolia.value?.hits?.find(
      (d) => d.slug === (citySlug.value || filters.value.city)
    ) as AlgoliaCity
)

if (eventListPageAttributes.value?.searchFilters?.tags) {
  filters.value.tags = eventListPageAttributes.value?.searchFilters?.tags.map(
    (d) => d.label
  )
}

const { currentOrderBy, currentPage } = useLocalizedOrderBy()
const hitsPerPagination = ref(HITS_PER_PAGINATION)

const shouldFetchCategories = (
  categories: CategoryTypes[] | undefined,
  oldCategories: CategoryTypes[] | undefined
) =>
  !categoryResults.value || (categories && isEqual(categories, oldCategories))

const fetchLocalitiesAndCategories = async (
  value: Partial<SearchFilterOptions>,
  oldValue: Partial<SearchFilterOptions> | undefined
) => {
  // Fetching localities and categories updates their corresponding
  // current value which are computed. This should update algoliaFitlerParams
  // This should trigger search
  shouldFetchCategories(value?.categories, oldValue?.categories)
  await searchCategories()
}

// This updates the amount of hits per pagination
// based on the amount of special giftcard events
// to keep a consistent amount of hits per pagination
const updateAmountOfHitsPerPagination = () => {
  if (specialGiftcardEvents.value?.length > 0) {
    hitsPerPagination.value = HITS_PER_PAGINATION - 1
  } else {
    hitsPerPagination.value = HITS_PER_PAGINATION
  }
}

// Running these only once before the actual Algolia Search
fetchLocalitiesAndCategories(defaultFilters.value, {})
updateAmountOfHitsPerPagination()

const currentIndexName = computed(
  () => currentOrderBy.value?.indexName || indexName
)

const algoliaSearch = ref(
  useAlgoliaSearch<AlgoliaEventItem>(currentIndexName.value)
)

const algoliaFilterParams = computed(() =>
  getAlgoliaSearchOptions({
    filters: filters.value,
    rawOptions: {
      hitsPerPage:
        HITS_PER_PAGINATION - (specialGiftcardEvents.value.length > 0 ? 1 : 0),
      page: currentPage.value - 1,
      /**
       * if online filter is active we need to show only online events
       * and ignore the locality
       */
      ...(filters.value?.city && !filters?.value?.online && !isNoResults.value
        ? {
            optionalFilters: [`city_slug: ${filters.value?.city}`],
          }
        : {
            aroundLatLngViaIP: import.meta.client,
            aroundRadius: 'all',
            aroundPrecision: 15000,
          }),
    },
  })
)

const {
  data: searchResults,
  status,
  execute: performSearch,
  error: searchError,
} = await useTransactionalPageAlgoliaSearch(
  algoliaSearch,
  currentIndexName,
  algoliaFilterParams,
  hitsPerPagination
)

const loading = computed(() => status.value === 'pending')

useTransactionalPageAlgoliaSearchSideEffects(
  currentIndexName,
  loading,
  algoliaSearch,
  performSearch,
  searchError
)

const algoliaQueryId = computed(() => searchResults.value?.queryID)

const filterOptions = computed(() => {
  return {
    ...searchResults.value?.facets,
    city: localitiesAlgolia.value?.hits,
  } as {
    [key: string]: any
    city: Array<Hit<AlgoliaCity>>
  }
})

const isFilterOpen = ref(false)
provide('isFilterOpen', isFilterOpen)

const zIndex = computed(() => {
  return isFilterOpen.value ? 'z-20 md:z-10' : 'z-[9]'
})

const sendFilterSelectedEvent = (filters: SearchFilterOptions) => {
  for (const [key, value] of Object.entries(filters)) {
    if (key === 'priceMin' || key === 'priceMax') continue

    if (!value || (Array.isArray(value) && value.length === 0)) continue

    const formatted = Array.isArray(value) ? value.join('; ') : value
    sendFilterSelected(key, `${formatted}`)
  }

  if (filters.priceMin && filters.priceMax) {
    sendFilterSelected('price', `${filters.priceMin}-${filters.priceMax}`)
  }
}

const handleFiltersUpdate = async (
  updatedFilters: SearchFilterOptions = {}
) => {
  // !!!
  sendFilterSelectedEvent(updatedFilters)

  const filteredCity = updatedFilters.city

  filters.value = {
    ...filters.value,
    ...updatedFilters,
  }

  if (
    [EventListPageType.CITY].includes(props.pageType) &&
    filteredCity &&
    filteredCity !== citySlug.value
  ) {
    return router.push({
      params: { city: filteredCity },
    })
  } else {
    // delete UNDEFINED and FALSE values and not put FALSE in query url
    const cleanFilters = Object.entries(filters.value).reduce(
      (acc, [key, value]) => {
        if (value !== undefined && value !== false) {
          acc[key] = value
        }
        return acc
      },
      {}
    )

    await router.push({
      query: {
        ...cleanFilters,
        page: 1, // reset page when filters update
      },
    })
  }
}

const handleChangePage = async (page: number) => {
  await router.push({ query: { ...router.currentRoute.value.query, page } })
  await performSearch()
}
const computedHits = computed(() => searchResults.value?.hits || [])

const eventCount = computed(() => searchResults.value?.nbHits)
const eventsCount = formattedNumberOfEvents(locale.value, NUMBER_OF_EVENTS)

const searchHeading = computed(
  () =>
    eventListPageAttributes.value?.searchHeading ||
    eventListPageAttributes.value?.title ||
    ''
)

if (props.loadSchema) {
  const { script: eventSchemas } = useAlgoliaEventSchema(
    searchResults.value?.hits || []
  )

  const { script: eventCarouselSchemas } = useAlgoliaEventCarouselSchema(
    eventSchemas.value.innerHTML['@graph']
  )

  const { shouldAppendTransactionalProductSchema, schemaObj } =
    await useTransactionalProductSchema({
      citySlug,
      tagSlug: computed(() => {
        if (props.pageType === EventListPageType.TEAMEVENT)
          return `${t('eventListPage.teamEventSlugPrefix')}-${citySlug.value}`

        return tagSlugParam.value?.toString()
      }),
      categorySlug,
      eventListPageData: props.eventListPageData,
      pageType: props.pageType,
      searchHeading,
      eventCount,
      hits: computedHits,
      reviewsData: props.reviewsData,
    })

  useTransactionalPageHead(
    props.pageType,
    props.eventListPageData!,
    {
      script: [
        ...(searchResults.value?.hits.length > 0 ? [eventCarouselSchemas] : []),
        ...(shouldAppendTransactionalProductSchema.value && eventCount.value
          ? [schemaObj]
          : []),
      ],
    },
    {
      hits: computedHits,
    }
  )
}

// Needs to be called after the filters are updated
// so that the requests work synchronously if on a page that
// does not load on server side
//
// Details on disclaimer about "server: false":
// https://nuxt.com/docs/getting-started/data-fetching#return-values
watch(
  () => defaultFilters.value,
  async (value, oldValue) => {
    if (!import.meta.client) return // We want to avoid watchers to force refetches on SSR as they might be in vain
    await fetchLocalitiesAndCategories(value, oldValue)
    await performSearch()
  },
  {
    immediate: true,
    deep: true,
  }
)

const updateFiltersStorage = () => {
  if (import.meta.client) {
    sessionStorage.setItem('filters', JSON.stringify(filters.value))
  }
}

const searchBasedOnFilterChanges = async () => {
  if (!import.meta.client) return // We want to avoid watchers to force refetches on SSR as they might be in vain
  updateAmountOfHitsPerPagination()
  updateFiltersStorage()
  await performSearch().finally(() => {
    setTimeout(async () => {
      if (isNoResults.value) {
        await performSearch()
      }
    }, 500)
  })
}

watch(() => filters.value, searchBasedOnFilterChanges, {
  immediate: true,
  deep: true,
})

watch(
  () => searchResults.value,
  (value) => {
    if (import.meta.client && value?.hits.length === 0) {
      isNoResults.value = true
      filters.value = cloneDeep(defaultFilters.value)
    }
  }
)
</script>

<style lang="postcss" scoped>
.filter-wrap {
  @apply pt-[11px] flex md:flex-row gap-2 md:gap-0 pb-3 max-md:overflow-x-auto max-md:overflow-y-visible;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none; /* For Chrome, Safari, and Edge */
  }
}
</style>
